// launchdarkly.service.ts
import { Injectable, Inject, InjectionToken, OnDestroy } from "@angular/core";
import {
  initialize,
  type LDClient,
  type LDContext,
  type LDUser
} from "launchdarkly-js-client-sdk";
import { BehaviorSubject, Observable } from "rxjs";
import { distinctUntilChanged } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { APP_NAME, APP_TENANT } from "src/app/setting/app-setting";

export type LDIdentity = LDUser | LDContext;
export type LDInitFn = (sdkKey: string, identity: LDIdentity) => LDClient;

export const LD_INIT = new InjectionToken<LDInitFn>("LD_INIT", {
  providedIn: "root",
  factory: () => initialize
});

@Injectable({ providedIn: "root" })
export class LaunchDarklyService implements OnDestroy {
  private ldClient!: LDClient;
  private flagsSubject = new BehaviorSubject<Record<string, any>>({});
  flags$: Observable<Record<string, any>> = this.flagsSubject
    .asObservable()
    .pipe(distinctUntilChanged());
  private ready = false;

  constructor(@Inject(LD_INIT) private ldInit: LDInitFn) {}

  async initialize(identity: LDIdentity): Promise<void> {
    const identityWithEnv = this.withEnv(identity);

    this.ldClient = this.ldInit(
      environment.launchDarklyClientSideID,
      identityWithEnv
    );
    await this.ldClient.waitForInitialization();
    this.ready = true;
    this.pushAllFlags();
    this.ldClient.on("change", (changes) => {
      const current = { ...this.flagsSubject.value };
      for (const key in changes) current[key] = changes[key].current;
      this.flagsSubject.next(current);
    });
  }

  async identify(identity: LDIdentity): Promise<void> {
    if (!this.ready) return this.initialize(identity);
    const identityWithEnv = this.withEnv(identity);

    await this.ldClient.identify(identityWithEnv);
    this.pushAllFlags();
  }

  getFlag(flagKey: string, defaultVal: any = false): any {
    const flags = this.flagsSubject.value;
    return Object.prototype.hasOwnProperty.call(flags, flagKey)
      ? flags[flagKey]
      : defaultVal;
  }

  ngOnDestroy() {
    this.ldClient?.close();
  }

  private getClientDomInfo() {
    const isBrowser = typeof window !== "undefined" && !!window.location;
    const hostname = isBrowser ? window.location.hostname : "server";
    return {
      domain: hostname.replace(/^www\./, ""), // normalize: drop www.
      origin: isBrowser ? window.location.origin : "server",
      path: isBrowser ? window.location.pathname : "",
      application: APP_NAME,
      tenant: APP_TENANT
    };
  }

  private getSafeDomain(): string {
    const { domain } = this.getClientDomInfo();
    // Ensure a stable, non-empty key (LaunchDarkly requires non-empty string)
    return domain && domain !== "server" ? domain : "unknown";
  }

  private buildEnvCtx() {
    const safeDomain = this.getSafeDomain();
    const { origin } = this.getClientDomInfo();
    return {
      kind: "env",
      key: environment.env,
      domain: safeDomain,
      application: APP_NAME,
      tenant: APP_TENANT,
      // keep but mark private via _meta below (or drop entirely if not needed)
      origin,
      _meta: { privateAttributes: ["origin"] }
    } as const;
  }

  private buildDomainCtx() {
    const safeDomain = this.getSafeDomain();
    return {
      kind: "domain",
      key: safeDomain
    } as const;
  }

  private hasKind(i: unknown): i is { kind: string } {
    return (
      !!i &&
      typeof i === "object" &&
      "kind" in (i as any) &&
      typeof (i as any).kind === "string"
    );
  }

  async addOrUpdateAccountContext(
    accountKey: string,
    attrs: Record<string, any> = {}
  ): Promise<void> {
    if (!this.ldClient) throw new Error("LD client not initialized");

    const current = await this.ldClient.getContext();

    let multi: any;
    if (!current || (current as any).kind !== "multi") {
      const k = (current as any)?.kind;
      multi = { kind: "multi" };
      if (k && typeof k === "string") {
        multi[k] = current;
      }
    } else {
      multi = { ...current };
    }

    multi.account = {
      kind: "account",
      key: accountKey,
      ...attrs
    };

    const next = this.withEnv(multi);

    await this.ldClient.identify(next);

    this.pushAllFlags();
  }

  /**
   * Ensures we always return an LD multi-context that includes:
   * - user or other single-kind identity provided by caller
   * - env context (kind: "env")
   * - domain context (kind: "domain")
   */
  private withEnv(identity: LDIdentity): LDContext {
    const envCtx = this.buildEnvCtx();
    const domainCtx = this.buildDomainCtx();

    // Legacy LDUser (no 'kind')
    if (!this.hasKind(identity)) {
      return {
        kind: "multi",
        user: identity as LDUser,
        env: envCtx,
        domain: domainCtx
      } as LDContext;
    }

    const kind = (identity as any).kind as string;

    if (kind === "multi") {
      // Merge/override env & domain while preserving existing contexts
      const multi = { ...(identity as any) };

      // If an env/domain already exist, shallow-merge; otherwise add fresh
      multi.env = multi.env ? { ...multi.env, ...envCtx } : envCtx;
      multi.domain = multi.domain
        ? { ...multi.domain, ...domainCtx }
        : domainCtx;

      return multi as LDContext;
    }

    if (kind === "user") {
      return {
        kind: "multi",
        user: identity as any,
        env: envCtx,
        domain: domainCtx
      } as LDContext;
    }

    // Single non-user context, e.g. { kind: "account", key: "..." }
    return {
      kind: "multi",
      [kind]: identity as any,
      env: envCtx,
      domain: domainCtx
    } as unknown as LDContext;
  }

  private pushAllFlags() {
    const all = this.ldClient.allFlags();
    this.flagsSubject.next(all);
  }
}
